// Core React and third-party libraries
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// Redux and state management
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';

// UI Components and styling
import Header from '../../Header/Header';
import { Icon, Loader, SuccessModal } from '@ohif/ui';
import { useTheme } from "../../../ui/src/contextProviders";

// Constants and utilities
import { _commonUrls, _machineUrls } from '@custom/Utils/constants/apiUrls';
import { _routes } from '@custom/Utils/constants/routes';
import { customStyle } from '@custom/Utils/utils';
import getModalityStudy from '@custom/rtk-apis/modalityStudyRegion/getModalityStudy';
import getModifiedCreditHospitals from '@custom/rtk-apis/modalityStudyRegion/getModifiedCreditHospitals';
import { closeModal } from '@custom/store/slices/modalityStudyRegion/getModifiedCreditHospitals';
import addTeleModPrice from '@custom/rtk-apis/Setting/addTeleModPrice';
import editTeleModPrice from '@custom/rtk-apis/Setting/editTeleModPrice';
import { servicesManager } from '../../../app/src/App';

// Define types for form values
interface ModalityPricingFormValues {
  modality: string;
  studyRegion: string;
  credit: number;
  exceptionHospitals: { id: string; useNewTariff: boolean; newTariff: number | null; oldCredit: number }[];
}

const AddEditModalityPricing = () => {
  // Theme context
  const isDarkMode = useTheme();

  // UI Notification Service
  const { uiNotificationService } = servicesManager.services;

  // Redux hooks
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const modalityData = location?.state?.modalityData || null;
  const isEditMode = location?.state?.editMode || false;

  const modalityAndStudyRegion = useSelector((state: RootState) => state.getModalityStudySliceResponse);
  const getModifiedCreditHospitalsData = useSelector((state: RootState) => state.getModifiedCreditHospitalsSliceResponse);

  // State management
  const [modal, setModal] = useState(false);
  const [message, setMessage] = useState('');
  const [modalities, setModalities] = useState([]);
  const [studyRegionList, setSetstudyRegionList] = useState([]);
  const [exceptionHospitals, setExceptionHospitals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // Initial form values
  const initialValues: ModalityPricingFormValues = {
    modality: modalityData?.modality || '',
    studyRegion: modalityData?.studyRegion || '',
    credit: modalityData?.credit ? Number(modalityData.credit) : 0,
    exceptionHospitals: exceptionHospitals.map((hospital) => ({
      id: hospital.id,
      useNewTariff: false, // Default to "No" (keep old credit)
      newTariff: null, // Empty unless "Yes" is selected
      oldCredit: hospital.credit, // Store old credit
    })),
  };
  // Form validation schema
  const validationSchema = Yup.object().shape({
    modality: Yup.string().required('Modality Name is required'),
    studyRegion: Yup.string()
      .when('modality', {
        is: (modality: string) => modality !== 'Histopathology',
        then: (schema) => schema.required('Study Region is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    credit: Yup.number().required('Modality Price is required'),
    exceptionHospitals: Yup.array().of(
      Yup.object({
        id: Yup.string().required(),
        useNewTariff: Yup.boolean().required(),
        newTariff: Yup.number().when('useNewTariff', {
          is: true,
          then: (schema) =>
            schema
              .required('New tariff is required')
              .positive('New tariff must be positive')
              .integer('New tariff must be an integer'),
          otherwise: (schema) => schema.nullable().notRequired(),
        }),
        oldCredit: Yup.number().required(),
      })
    ),
  });

  /**
   * Handles form submission
   * @param formValues - The form values to be saved
   */
  const handleSave = async (formValues: ModalityPricingFormValues) => {
    try {
      setIsLoading(true);

      // Prepare payload
      const payload = {
        ...formValues,
        // Override studyRegion ONLY if modality is "Histopathology"
        ...(formValues.modality === 'Histopathology' && { studyRegion: 'Histopathology' }),
        credit: Number(formValues.credit),
        // Include _id in edit mode
        ...(isEditMode && { _id: modalityData?._id }),
        // Include exceptionHospitals if any
        ...(isEditMode && {
          exceptionHospitals: formValues.exceptionHospitals.map((hospital) => ({
            _id: hospital.id,
            useNewTariff: hospital.useNewTariff,
            credit: hospital.useNewTariff ? Number(hospital.newTariff) : hospital.oldCredit,
          })),
        }),
      };

      // Dispatch API call
      const response = isEditMode ? await dispatch(editTeleModPrice(payload)).unwrap() : await dispatch(addTeleModPrice(payload)).unwrap();

      // Success case
      setMessage(
        isEditMode
          ? 'Modality pricing updated successfully'
          : 'Modality pricing saved successfully'
      );
      setModal(true);
      setExceptionHospitals([]);
      dispatch(closeModal());
      setTimeout(() => navigate(_routes.settings.modalityPrice), 1700);
    } catch (error: any) {
      console.error('Error saving modality pricing:', error);
      uiNotificationService.show({
        title: 'Credit',
        message: error?.msg || 'Failed to save modality pricing',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles cancel action and navigates back to modality pricing list
   */
  const handleCancel = () => {
    setExceptionHospitals([]);
    dispatch(closeModal());
    navigate(_routes.settings.modalityPrice);
  };

  useEffect(() => {
    if (modalityAndStudyRegion.status === 'SUCCESS' && modalityAndStudyRegion.result) {
      setModalities(modalityAndStudyRegion.result.modalities);
      setSetstudyRegionList(modalityAndStudyRegion.result.studyRegions);
    }
  }, [modalityAndStudyRegion.result, modalityAndStudyRegion.status, modalityAndStudyRegion.error]);

  useEffect(() => {
    if (modalities?.length > 0 && !modalities.some(m => m === 'Histopathology')) {
      // console.log('first time', modalities);
      setModalities((curr) => [...curr, 'Histopathology']);
    }
  }, [modalities]);
  useEffect(() => {
    if (getModifiedCreditHospitalsData.status === 'SUCCESS' && getModifiedCreditHospitalsData.result) {
      const data = getModifiedCreditHospitalsData?.result?.map((hospital: any) => ({
        id: hospital?._id || '',
        name: hospital?.name || '',
        credit: hospital?.matchedTeleCredit?.credit || 0,
      }));
      setExceptionHospitals(data);
    }
  }, [getModifiedCreditHospitalsData.result, getModifiedCreditHospitalsData.status, getModifiedCreditHospitalsData.error]);

  useEffect(() => {
    dispatch(getModalityStudy());
    if (isEditMode) {
      dispatch(getModifiedCreditHospitals(modalityData?._id));
    }
  }, []);

  return (
    <>
      <div
        className='page page__full'
        style={isDarkMode?.isDarkMode ? { background: '#090c29' } : customStyle.mainBackground}
      >
        <Header
          isBack={true}
          title={isEditMode ? 'Edit Credit' : 'Add Credit'}
        />
        <div className='page__body'>
          <div className='details'>
            <div className='form form--innersec'>
              <section className='form__section'>
                <h1 className='form__section__title'>Credit Details</h1>
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  enableReinitialize={true}
                  onSubmit={handleSave}
                >
                  {({ isSubmitting, values, setFieldValue }) => (
                    <Form>
                      <div className='formRow formRow__3'>
                        <div className='formCol'>
                          <div className="form__Field">
                            <label className="form__label" htmlFor='modality'>
                              Modality
                              <span className="required-asterisk">*</span>
                            </label>
                            <Field
                              as="select"
                              className={`form__select form__select--sm ${isEditMode ? 'disabled-input' : ''}`}
                              style={{
                                ...customStyle.select,
                                ...(isEditMode && { cursor: 'not-allowed' })
                              }}
                              name="modality"
                              disabled={isEditMode}
                              aria-disabled={isEditMode}
                            >
                              <option value="">-Select-</option>
                              {modalities?.map((modality) => (
                                <option key={modality} value={modality}>
                                  {modality}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage name="modality" component="div" className="error text-error" />
                          </div>
                        </div>
                        {
                          values.modality !== 'Histopathology' &&
                          <div className='formCol'>
                            <div className="form__Field">
                              <label className="form__label" htmlFor='studyRegion'>
                                Study Region
                                <span className="required-asterisk">*</span>
                              </label>
                              <Field
                                as="select"
                                className={`form__select form__select--sm ${isEditMode ? 'disabled-input' : ''}`}
                                style={{
                                  ...customStyle.select,
                                  ...(isEditMode && { cursor: 'not-allowed' })
                                }}
                                name="studyRegion"
                                disabled={isEditMode}
                                aria-disabled={isEditMode}
                              >
                                <option value="">-Select-</option>
                                {studyRegionList?.map((region) => (
                                  <option key={region} value={region}>
                                    {region}
                                  </option>
                                ))}
                              </Field>
                              <ErrorMessage name="studyRegion" component="div" className="error text-error" />
                            </div>
                          </div>
                        }
                        <div className='formCol'>
                          <div className="form__Field">
                            <label className='form__label' htmlFor='credit'>
                              Credits
                              <span className="required-asterisk">*</span>
                            </label>
                            <Field
                              className="form__input form__input--sm"
                              type="number"
                              id="credit"
                              name="credit"
                              placeholder="Credits"
                              autoComplete='off'
                            />
                            <ErrorMessage name="credit" component="div" className="error text-error" />
                          </div>
                        </div>
                      </div>
                      {isEditMode && exceptionHospitals.length > 0 ? (
                        <div className="form__Field">
                          <label
                            className="form__label"
                            style={{
                              display: 'flex',
                              fontWeight: 600,
                              fontSize: "1rem",
                              marginTop: "1.5rem",
                              marginBottom: "1.5rem",
                              alignItems: 'center',

                            }}
                          >
                            Exception Hospitals <Icon
                              className="cursor-pointer"
                              name="info"
                              style={{
                                width: "1rem",
                                height: "1rem",
                                marginLeft: '0.5rem',
                              }}
                            ></Icon>
                          </label>
                          <div className="mb-[12px] flex flex-wrap gap-8 sm:flex-col sm:gap-3" >
                            {exceptionHospitals.map((hospital, index) => (
                              <div
                                className="formCol"
                                key={hospital.id}
                                style={{ maxWidth: '400px' }}
                              >
                                <div
                                  className="form__Field"
                                  style={{
                                    padding: '0.75rem',
                                    backgroundColor: isDarkMode?.isDarkMode ? '#090c29' : '#f6ffff',
                                    border: `2px solid ${isDarkMode?.isDarkMode ? '#FFFFFF3D' : 'rgba(15, 132, 145, 0.24)'}`,
                                    borderRadius: '6px',
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: '0.875rem',
                                      fontWeight: 600,
                                      color: isDarkMode?.isDarkMode ? '#E2E8F0' : '#1A202C',
                                      marginBottom: '0.5rem',
                                    }}
                                  >
                                    {hospital.name} (Credit: {hospital.credit})
                                  </div>
                                  <div className="form__radio">
                                    {[
                                      { value: 'true', label: 'New Tariff' },
                                      { value: 'false', label: 'Old Tariff ' },
                                    ].map((option) => (
                                      <label
                                        key={option.value}
                                        className="flex items-center mr-8 custom-radio-container"
                                      >
                                        <Field
                                          type="radio"
                                          name={`exceptionHospitals[${index}].useNewTariff`}
                                          value={option.value}
                                          checked={String(values.exceptionHospitals[index]?.useNewTariff) === option.value}
                                          onChange={() => {
                                            setFieldValue(`exceptionHospitals[${index}].useNewTariff`, option.value === 'true');
                                            if (option.value === 'false') {
                                              setFieldValue(`exceptionHospitals[${index}].newTariff`, null);
                                            }
                                          }}
                                          className="form__radio__input"
                                        />
                                        <span className="form__radio__label">{option.label}</span>
                                      </label>
                                    ))}
                                  </div>
                                  {values.exceptionHospitals[index]?.useNewTariff && (
                                    <div className="form__Field">
                                      <label
                                        className="form__label"
                                        htmlFor={`exceptionHospitals[${index}].newTariff`}
                                      >
                                        New Tariff
                                        <span className="required-asterisk">*</span>
                                      </label>
                                      <Field
                                        className="form__input form__input--sm"
                                        type="number"
                                        id={`exceptionHospitals[${index}].newTariff`}
                                        name={`exceptionHospitals[${index}].newTariff`}
                                        placeholder="New Tariff"
                                        autoComplete="off"
                                      />
                                      <ErrorMessage
                                        name={`exceptionHospitals[${index}].newTariff`}
                                        component="div"
                                        className="error text-error"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : isEditMode ? (
                        <div className="formCol">
                          <div className="form__Field" style={{ marginBottom: '1.5rem' }}>
                            <label
                              className="form__label"
                              style={{
                                display: 'flex',
                                fontWeight: 600,
                                fontSize: "1rem",
                                marginTop: "1.5rem",
                                marginBottom: "1.5rem",
                                alignItems: 'center',

                              }}
                            >
                              Exception Hospitals <Icon
                                className="cursor-pointer"
                                name="info"
                                style={{
                                  width: "1rem",
                                  height: "1rem",
                                  marginLeft: '0.5rem',
                                }}
                              ></Icon>
                            </label>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: isDarkMode?.isDarkMode ? '#090c29' : '#F6FFFF',
                                border: `1px solid ${isDarkMode?.isDarkMode ? '#2D3748' : '#E2E8F0'}`,
                                borderRadius: '8px',
                                padding: '1rem',
                                gap: '0.25rem',
                              }}
                            >
                              <h3
                                style={{
                                  fontSize: '1rem',
                                  fontWeight: 600,
                                  margin: 0,
                                  color: isDarkMode?.isDarkMode ? '#E2E8F0' : '#1A202C',
                                }}
                              >
                                No Exception Hospitals
                              </h3>
                              <p
                                style={{
                                  fontSize: '0.875rem',
                                  color: isDarkMode?.isDarkMode ? '#A0AEC0' : '#4A5568',
                                  margin: 0,
                                  lineHeight: 1.5,
                                }}
                              >
                                The common credit value applies to all hospitals for this modality, as no hospital-specific credits have been set.
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <div className='btn__group'>
                        <button
                          className='btn btn__primary'
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isEditMode ? "Update" : "Save"}
                        </button>
                        <button
                          type="button"
                          className='btn btn__white'
                          onClick={handleCancel}
                        >
                          Cancel
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </section>
            </div>
          </div>
        </div>
        <SuccessModal
          isSuccessModalOpen={modal}
          title={message}
          handleCloseModal={() => setModal(false)}
        />
        <Loader isLoaderOpen={isLoading} />
      </div>
    </>
  );
};

export default AddEditModalityPricing;
